import React, { useState, useEffect } from "react";
import Inputs from "../../basics/Inputs";
import Dropdown from "../../../Form/FormDropdown";
import { Form } from "react-bootstrap";
import CheckBoxInputGroup from "../../basics/CheckBoxInputGroup";
import { checkForNull, validateFormElements } from "../../../../utilities/formValidators";
import { DROPDOWN_IDS } from "../../common/DropdownValues";
import { propertyUsageTypes } from "../../common/constants";
import Update from "./Update";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import Title from '../../basics/Title';
import useLanguage from "../../../../utilities/useLanguage";
import { getTranslation } from "../../translations";
import { AutomationPlacement } from "../../../../utilities/automation";

const FormContainer = styled.div`
    .error {
        color: ${props => props.theme.primary};
        padding-left: 22px;
        font-size: 16px;
    }

    @media(max-width:766px) {
        h3{
            text-align: center;
        }
    }
`;

export default (props: any) => {
    const { values, submitForm, dropdownState, labels, errorValue, formLoading, firstTimeLimit, maxLTVRates, loanType } = props;
    const [toggleButton, setToggle] = useState(true);
    const defaultError = { isError: false, message: "" };
    const [error, setError] = useState(defaultError);
    const { lang } = useLanguage();
    const isVALoan = loanType === 'VA';

    useEffect(() => {
        if (errorValue) {
            setError(errorValue);
        }
    }, [errorValue]);

    useEffect(() => {
        const maxLTV = maxLTVRates.data[values.LoanTerm.value] ?? maxLTVRates.data.default;
        const tempError = validateFormElements(
            values.LoanPurpose.value,
            parseInt(values.DownPayment.value),
            parseInt(values.RefinanceAmount.value),
            parseInt(values.PurchasePrice.value),
            parseInt(values.CurrentHomeValue.value),
            parseInt(values.LoanTerm.value),
            values.FirstTimeLimit.value,
            values.LoanLimit.value,
            maxLTV,
        );

        if (tempError.isError) {
            setError(tempError);
            toggleUpdateButton(true);
        } else {
            toggleUpdateButton(checkForNull(values));
            setError(defaultError);
        }
    }, [values, firstTimeLimit]);

    function handleSubmit() {
        submitForm();
        toggleUpdateButton(true);
    }

    function toggleUpdateButton(disable) {
        if (!disable) {
            setToggle(false);
        } else {
            setToggle(true);
        }
    }

    function handleDropdownChange(e) {
        const { name, value } = e.target;
        handleInputChange(name, value);
    }

    function handleInputChange(name, value, required = true) {
        props.onChange(name, value, required);
    }

    function handleCheckboxChange(key, value, required) {
        props.onChange(key, value, required);
    }

    // Checks for disabled options when the loan is property usage type
    function checkUsageTypeDisabledOptions(value) {
        return value === propertyUsageTypes.SECOND_HOME || value === propertyUsageTypes.PRIMARY_RESIDENCE
    }

    function CalculatorDropdown({ id, className = "", disabled = false}) {
        const options = dropdownState[id].data;
        const defaultValue = values[id].value || null;

        return (
            <Dropdown
                id={id}
                placeholder={dropdownState[id].name}
                options={options}
                require={true}
                onChange={handleDropdownChange}
                defaultValue={defaultValue}
                className={className}
                disabled={disabled}
            />
        );
    }

    function Error({ error }) {
        return (
            <div className={error.isError ? "error mb-4" : "d-none"}>
                <FontAwesomeIcon icon={faExclamationCircle} size="lg" />&nbsp;{error.message}
            </div>
        );
    }

    const infoProps = { className: "info-grey", disclaimer: "here is a disclaimer" };
    const updateProps = { toggleButton, handleSubmit, formLoading };

    return (
        <FormContainer>
            <Title>{getTranslation(lang).loanAssumptions}</Title>
            <Form noValidate >
                <div className="row">
                    <div className="col-lg-4 col-md-6 col-12 px-5 pt-3 pb-0 pb-md-3 pt-md-3 border-md-border-0  border-md-right">
                        <CalculatorDropdown id={DROPDOWN_IDS.LOAN_PURPOSE} />
                        <CalculatorDropdown id={DROPDOWN_IDS.LOAN_TERM} />
                        {isVALoan && <CalculatorDropdown id={DROPDOWN_IDS.VETERAN_TYPE} />}
                        <Inputs containerClassName={isVALoan ? "col-6 float-left pl-0" : ""} labels={labels} values={values} onChange={handleInputChange} placement={AutomationPlacement.MODAL} />
                    </div>
                    <div className="col-lg-4 col-md-6 col-12 px-5 pt-3 pt-md-3 pb-0 pb-md-3 border-md-border-0  border-lg-right">
                        <CalculatorDropdown id={DROPDOWN_IDS.USAGE_TYPE} />
                        <CalculatorDropdown id={DROPDOWN_IDS.PROPERTY_TYPE} />
                        <CalculatorDropdown id={DROPDOWN_IDS.STATE} className="col-6 float-left pl-0" />
                        <CalculatorDropdown id={DROPDOWN_IDS.COUNTY} className="col-6 float-left pr-0" disabled={!values["State"].value || formLoading} />
                        <CalculatorDropdown id={DROPDOWN_IDS.CREDIT_SCORE} />
                    </div>
                    <div className="col-lg-4 col-md-6 col-12 mortgage-detail-last-col px-5 py-3 pt-md-3 macu">
                        <CheckBoxInputGroup value={values.PropertyTax.value} label={getTranslation(lang).includePropertyTax} name="includePropertyTax" inputName="PropertyTax" required={false} onChange={handleCheckboxChange} {...infoProps} />
                        <CheckBoxInputGroup value={values.Insurance.value} label={getTranslation(lang).includeInsurance} name="includeInsurance" inputName="Insurance" required={false} onChange={handleCheckboxChange} {...infoProps} />
                        <Update {...updateProps } />
                    </div>
                </div>
            </Form>
            <Error error={error} />
        </FormContainer>
    );
};